<template>
  <div class="min-h-screen flex-1 mt-1 bg-gray-100 px-4 pt-16 shadow-lg items-center">
    <div class="bg-white w-full rounded-lg shadow">
      <!-- Header -->
      <div class="h-20 py-3 flex items-center border-gray-200 p-6 mt-5">
        <div class="flex">
          <button
            :class="$colors.primary"
            class="rounded-full flex justify-center items-center w-8 h-8 mr-3"
            @click="$router.back()"
          >
            <i class="material-icons">chevron_left</i>
          </button>
          <div v-if="!formData._id" class="text-2xl font-bold text-green-500">
            {{ $t('new_partner') || 'Nouveau Partenaire' }}
          </div>
          <div v-else class="text-2xl font-bold text-green-500">
            {{ $t('edit_partner') || 'Modifier Partenaire' }}
          </div>
        </div>
      </div>

      <div class="relative">
        <loading :active.sync="loading" :is-full-page="false"></loading>

        <div class="bg-white rounded-lg p-6">
          <!-- Section: Informations de base -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
              <i class="material-icons text-sm mr-2">business</i>
              {{ $t('basic_info') || 'Informations de base' }}
            </h3>
            <div class="grid lg:grid-cols-2 gap-6">
              <!-- Company Name -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('company_name') || 'Nom de l\'entreprise' }} *</label>
                </div>
                <input
                  type="text"
                  v-model="formData.partnerConfig.companyName"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  required
                />
              </div>

              <!-- Full Name -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('full_name') || 'Nom complet' }} *</label>
                </div>
                <input
                  type="text"
                  v-model="formData.fullName"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  required
                />
              </div>

              <!-- Email -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('email') || 'Email' }} *</label>
                </div>
                <input
                  type="email"
                  v-model="formData.email"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  required
                  :disabled="!!formData._id"
                />
              </div>

              <!-- Phone -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('phone') || 'Téléphone' }} *</label>
                </div>
                <input
                  type="text"
                  v-model="formData.phone"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  required
                />
              </div>

              <!-- Password (only for new partners) -->
              <div v-if="!formData._id" class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('password') || 'Mot de passe' }} *</label>
                </div>
                <input
                  type="password"
                  v-model="formData.password"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  required
                />
              </div>

              <!-- Status -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('status') || 'Statut' }}</label>
                </div>
                <select
                  v-model="formData.status"
                  class="py-2 text-xs px-2 outline-none block h-full w-full bg-white"
                >
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                  <option value="blocked">Bloqué</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Section: Configuration Contrat -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
              <i class="material-icons text-sm mr-2">description</i>
              {{ $t('contract_config') || 'Configuration du contrat' }}
            </h3>
            <div class="grid lg:grid-cols-3 gap-6">
              <!-- Contract Type -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('contract_type') || 'Type de contrat' }}</label>
                </div>
                <select
                  v-model="formData.partnerConfig.contractType"
                  @change="updateRateLimits"
                  class="py-2 text-xs px-2 outline-none block h-full w-full bg-white"
                >
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <!-- Contract Start Date -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('start_date') || 'Date de début' }}</label>
                </div>
                <input
                  type="date"
                  v-model="formData.partnerConfig.contractStartDate"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                />
              </div>

              <!-- Contract End Date -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('end_date') || 'Date de fin' }}</label>
                </div>
                <input
                  type="date"
                  v-model="formData.partnerConfig.contractEndDate"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                />
              </div>
            </div>
          </div>

          <!-- Section: Rate Limiting -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
              <i class="material-icons text-sm mr-2">speed</i>
              {{ $t('rate_limiting') || 'Limitations de débit' }}
            </h3>
            <div class="grid lg:grid-cols-3 gap-6">
              <!-- Requests per minute -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('requests_per_minute') || 'Requêtes/minute' }}</label>
                </div>
                <input
                  type="number"
                  v-model.number="formData.partnerConfig.rateLimit.requestsPerMinute"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  min="1"
                />
              </div>

              <!-- Requests per day -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('requests_per_day') || 'Requêtes/jour' }}</label>
                </div>
                <input
                  type="number"
                  v-model.number="formData.partnerConfig.rateLimit.requestsPerDay"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  min="1"
                />
              </div>

              <!-- Orders per day -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('orders_per_day') || 'Commandes/jour' }}</label>
                </div>
                <input
                  type="number"
                  v-model.number="formData.partnerConfig.rateLimit.ordersPerDay"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  min="1"
                />
              </div>
            </div>
          </div>

          <!-- Section: Webhook Configuration -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
              <i class="material-icons text-sm mr-2">webhook</i>
              {{ $t('webhook_config') || 'Configuration Webhook' }}
            </h3>
            <div class="grid lg:grid-cols-1 gap-6">
              <!-- Webhook URL -->
              <div class="border focus-within:border-green-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <label class="bg-white text-gray-700 px-1">{{ $t('webhook_url') || 'URL Webhook' }}</label>
                </div>
                <input
                  type="url"
                  v-model="formData.partnerConfig.webhookUrl"
                  placeholder="https://api.partner.com/webhooks"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                />
              </div>

              <!-- Webhook Events -->
              <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('webhook_events') || 'Événements webhook' }}</label>
                <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <label v-for="event in availableEvents" :key="event" class="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      :value="event"
                      v-model="formData.partnerConfig.webhookEvents"
                      class="form-checkbox h-4 w-4 text-green-600"
                    />
                    <span class="text-sm text-gray-700">{{ formatEventName(event) }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Section: IP Whitelist -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
              <i class="material-icons text-sm mr-2">security</i>
              {{ $t('ip_whitelist') || 'Liste blanche IP' }}
            </h3>
            <div class="space-y-2">
              <div v-for="(ip, index) in formData.partnerConfig.allowedIPs" :key="index" class="flex items-center space-x-2">
                <input
                  type="text"
                  v-model="formData.partnerConfig.allowedIPs[index]"
                  placeholder="192.168.1.1"
                  class="flex-1 py-2 text-xs px-2 outline-none border rounded"
                />
                <button
                  @click="removeIP(index)"
                  class="p-2 text-red-500 hover:text-red-700"
                >
                  <i class="material-icons text-sm">delete</i>
                </button>
              </div>
              <button
                @click="addIP"
                class="flex items-center text-green-600 hover:text-green-800"
              >
                <i class="material-icons text-sm mr-1">add</i>
                {{ $t('add_ip') || 'Ajouter une IP' }}
              </button>
              <p class="text-xs text-gray-500">
                {{ $t('ip_whitelist_info') || 'Laissez vide pour autoriser toutes les IPs' }}
              </p>
            </div>
          </div>

          <!-- Section: Tarification (Fees) -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
              <i class="material-icons text-sm mr-2">attach_money</i>
              {{ $t('pricing') || 'Tarification B2B' }}
            </h3>
            <div v-for="(fee, index) in formData.fees" :key="index" class="border rounded p-4 mb-4">
              <div class="flex justify-between items-center mb-4">
                <span class="font-medium text-gray-700">{{ getWarehouseName(fee.warehouse) }}</span>
                <button @click="removeFee(index)" class="text-red-500 hover:text-red-700">
                  <i class="material-icons text-sm">delete</i>
                </button>
              </div>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label class="block text-xs text-gray-600 mb-1">Confirmation</label>
                  <input type="number" v-model.number="fee.confirmation" class="w-full px-2 py-1 text-xs border rounded" />
                </div>
                <div>
                  <label class="block text-xs text-gray-600 mb-1">Fulfillment</label>
                  <input type="number" v-model.number="fee.fulfillement_fees" class="w-full px-2 py-1 text-xs border rounded" />
                </div>
                <div>
                  <label class="block text-xs text-gray-600 mb-1">Shipping</label>
                  <input type="number" v-model.number="fee.shipping" class="w-full px-2 py-1 text-xs border rounded" />
                </div>
                <div>
                  <label class="block text-xs text-gray-600 mb-1">Refund</label>
                  <input type="number" v-model.number="fee.refund" class="w-full px-2 py-1 text-xs border rounded" />
                </div>
                <div>
                  <label class="block text-xs text-gray-600 mb-1">Change</label>
                  <input type="number" v-model.number="fee.change" class="w-full px-2 py-1 text-xs border rounded" />
                </div>
                <div>
                  <label class="block text-xs text-gray-600 mb-1">Cancelation</label>
                  <input type="number" v-model.number="fee.cancelation" class="w-full px-2 py-1 text-xs border rounded" />
                </div>
                <div>
                  <label class="block text-xs text-gray-600 mb-1">Commission %</label>
                  <input type="number" v-model.number="fee.commission_percentage" class="w-full px-2 py-1 text-xs border rounded" step="0.1" />
                </div>
                <div>
                  <label class="block text-xs text-gray-600 mb-1">Stock Fees</label>
                  <input type="number" v-model.number="fee.stock_fee" class="w-full px-2 py-1 text-xs border rounded" />
                </div>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <select v-model="selectedWarehouse" class="flex-1 px-2 py-2 text-xs border rounded">
                <option value="">{{ $t('select_warehouse') || 'Sélectionner un entrepôt' }}</option>
                <option v-for="wh in availableWarehouses" :key="wh._id" :value="wh._id">
                  {{ wh.name }} ({{ wh.country }})
                </option>
              </select>
              <button
                @click="addFee"
                :disabled="!selectedWarehouse"
                class="px-4 py-2 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
              >
                {{ $t('add_pricing') || 'Ajouter tarification' }}
              </button>
            </div>
          </div>

          <!-- Section: Countries -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
              <i class="material-icons text-sm mr-2">public</i>
              {{ $t('countries') || 'Pays autorisés' }}
            </h3>
            <div class="grid grid-cols-3 md:grid-cols-6 gap-4">
              <label v-for="country in availableCountries" :key="country.code" class="flex items-center space-x-2">
                <input
                  type="checkbox"
                  :value="country.code"
                  v-model="formData.countries"
                  class="form-checkbox h-4 w-4 text-green-600"
                />
                <span class="text-sm text-gray-700">{{ country.name }}</span>
              </label>
            </div>
          </div>

          <!-- Submit Buttons -->
          <div class="flex justify-end space-x-4 pt-6 border-t">
            <button
              @click="$router.back()"
              class="px-6 py-2 border rounded text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {{ $t('cancel') || 'Annuler' }}
            </button>
            <button
              @click="save"
              :disabled="saving"
              class="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <span v-if="saving">{{ $t('saving') || 'Enregistrement...' }}</span>
              <span v-else>{{ formData._id ? ($t('update') || 'Mettre à jour') : ($t('create') || 'Créer') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Modal with API Keys -->
    <div v-if="showSuccessModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
        <div class="text-center mb-4">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i class="material-icons text-green-600 text-3xl">check</i>
          </div>
          <h3 class="text-lg font-bold text-gray-900">{{ $t('partner_created') || 'Partenaire créé avec succès!' }}</h3>
        </div>
        <div v-if="createdCredentials" class="space-y-4 bg-gray-50 p-4 rounded">
          <p class="text-sm text-red-600 font-medium text-center">
            {{ $t('save_credentials_warning') || 'Sauvegardez ces informations, elles ne seront plus affichées!' }}
          </p>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Key</label>
            <code class="block bg-white p-2 rounded border text-xs break-all">{{ createdCredentials.apiKey }}</code>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Secret</label>
            <code class="block bg-white p-2 rounded border text-xs break-all">{{ createdCredentials.apiSecret }}</code>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Webhook Secret</label>
            <code class="block bg-white p-2 rounded border text-xs break-all">{{ createdCredentials.webhookSecret }}</code>
          </div>
        </div>
        <div class="mt-6 text-center">
          <button
            @click="closeSuccessModal"
            class="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
          >
            {{ $t('understood') || 'J\'ai compris' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object }
  },
  data() {
    return {
      loading: false,
      saving: false,
      formData: {
        fullName: '',
        email: '',
        phone: '',
        password: '',
        status: 'active',
        countries: [],
        fees: [],
        partnerConfig: {
          companyName: '',
          contractType: 'standard',
          contractStartDate: new Date().toISOString().split('T')[0],
          contractEndDate: '',
          isActive: true,
          webhookUrl: '',
          webhookEvents: ['order.created', 'order.status_changed', 'shipping.status_changed', 'shipping.delivered'],
          allowedIPs: [],
          rateLimit: {
            requestsPerMinute: 60,
            requestsPerDay: 10000,
            ordersPerDay: 1000
          }
        }
      },
      warehouses: [],
      selectedWarehouse: '',
      showSuccessModal: false,
      createdCredentials: null,
      availableEvents: [
        'order.created',
        'order.status_changed',
        'shipping.created',
        'shipping.status_changed',
        'shipping.delivered',
        'shipping.returned'
      ],
      countryNames: {
        'MA': 'Maroc',
        'TN': 'Tunisie',
        'DZ': 'Algérie',
        'EG': 'Egypte',
        'SA': 'Arabie Saoudite',
        'AE': 'Emirats',
        'CI': 'Côte d\'Ivoire',
        'SN': 'Sénégal',
        'CM': 'Cameroun',
        'ML': 'Mali',
        'BF': 'Burkina Faso',
        'GN': 'Guinée',
        'BJ': 'Bénin',
        'TG': 'Togo',
        'NE': 'Niger',
        'GA': 'Gabon',
        'CG': 'Congo',
        'CD': 'RD Congo',
        'NG': 'Nigeria',
        'GH': 'Ghana',
        'KE': 'Kenya',
        'ZA': 'Afrique du Sud'
      }
    };
  },
  computed: {
    availableWarehouses() {
      const usedIds = this.formData.fees.map(f => f.warehouse);
      return this.warehouses.filter(w => !usedIds.includes(w._id));
    },
    availableCountries() {
      // Extract unique countries from warehouses
      const countries = [];
      const seenCodes = new Set();

      this.warehouses.forEach(wh => {
        if (wh.country && !seenCodes.has(wh.country)) {
          seenCodes.add(wh.country);
          countries.push({
            code: wh.country,
            name: this.countryNames[wh.country] || wh.countryName || wh.country
          });
        }
      });

      // Sort by name
      return countries.sort(function(a, b) {
        return a.name.localeCompare(b.name);
      });
    }
  },
  async mounted() {
    await this.fetchWarehouses();
    if (this.$route.params.id) {
      await this.fetchPartner(this.$route.params.id);
    }
  },
  methods: {
    async fetchWarehouses() {
      try {
        const response = await this.$server.search('warehouses', { limit: 100 });
        if (response && response.content) {
          this.warehouses = response.content.results || response.content || [];
        }
      } catch (error) {
        console.error('Error fetching warehouses:', error);
      }
    },
    async fetchPartner(id) {
      this.loading = true;
      try {
        const response = await this.$server.get('partners', { partnerId: id });
        if (response && response.content) {
          const partner = response.content;
          const config = partner.partnerConfig || {};
          this.formData = {
            _id: partner._id,
            fullName: partner.fullName || '',
            email: partner.email || '',
            phone: partner.phone || '',
            status: partner.status || 'active',
            countries: partner.countries || [],
            fees: partner.fees || [],
            partnerConfig: {
              companyName: config.companyName || '',
              contractType: config.contractType || 'standard',
              contractStartDate: config.contractStartDate ? new Date(config.contractStartDate).toISOString().split('T')[0] : '',
              contractEndDate: config.contractEndDate ? new Date(config.contractEndDate).toISOString().split('T')[0] : '',
              isActive: config.isActive !== undefined ? config.isActive : true,
              webhookUrl: config.webhookUrl || '',
              webhookEvents: config.webhookEvents || [],
              allowedIPs: config.allowedIPs || [],
              rateLimit: config.rateLimit || {
                requestsPerMinute: 60,
                requestsPerDay: 10000,
                ordersPerDay: 1000
              }
            }
          };
        }
      } catch (error) {
        console.error('Error fetching partner:', error);
        this.$toast.error(this.$t('error_fetching_partner') || 'Erreur lors du chargement du partenaire');
      } finally {
        this.loading = false;
      }
    },
    updateRateLimits() {
      const limits = {
        standard: { requestsPerMinute: 60, requestsPerDay: 10000, ordersPerDay: 1000 },
        premium: { requestsPerMinute: 90, requestsPerDay: 25000, ordersPerDay: 2500 },
        enterprise: { requestsPerMinute: 120, requestsPerDay: 50000, ordersPerDay: 5000 }
      };
      const type = this.formData.partnerConfig.contractType;
      this.formData.partnerConfig.rateLimit = limits[type] || limits.standard;
    },
    addIP() {
      this.formData.partnerConfig.allowedIPs.push('');
    },
    removeIP(index) {
      this.formData.partnerConfig.allowedIPs.splice(index, 1);
    },
    addFee() {
      if (!this.selectedWarehouse) return;
      this.formData.fees.push({
        warehouse: this.selectedWarehouse,
        confirmation: -1,
        fulfillement_fees: -1,
        shipping: -1,
        refund: -1,
        change: -1,
        cancelation: -1,
        quality_control: -1,
        stock_fee: -1,
        commission_percentage: -1
      });
      this.selectedWarehouse = '';
    },
    removeFee(index) {
      this.formData.fees.splice(index, 1);
    },
    getWarehouseName(id) {
      const wh = this.warehouses.find(w => w._id === id);
      return wh ? `${wh.name || wh.countryName} (${wh.country})` : id;
    },
    formatEventName(event) {
      const names = {
        'order.created': 'Commande créée',
        'order.status_changed': 'Statut commande changé',
        'shipping.created': 'Expédition créée',
        'shipping.status_changed': 'Statut expédition changé',
        'shipping.delivered': 'Livré',
        'shipping.returned': 'Retourné'
      };
      return names[event] || event;
    },
    async save() {
      // Validation
      if (!this.formData.partnerConfig.companyName) {
        this.$toast.error(this.$t('company_name_required') || 'Le nom de l\'entreprise est requis');
        return;
      }
      if (!this.formData.email) {
        this.$toast.error(this.$t('email_required') || 'L\'email est requis');
        return;
      }
      if (!this.formData.phone) {
        this.$toast.error(this.$t('phone_required') || 'Le téléphone est requis');
        return;
      }
      if (!this.formData._id && !this.formData.password) {
        this.$toast.error(this.$t('password_required') || 'Le mot de passe est requis');
        return;
      }

      // Filter out empty IPs
      this.formData.partnerConfig.allowedIPs = this.formData.partnerConfig.allowedIPs.filter(ip => ip && ip.trim());

      this.saving = true;
      try {
        let response;
        if (this.formData._id) {
          response = await this.$server.update('partners', {
            partnerId: this.formData._id,
            ...this.formData
          });
          if (response && response.content) {
            this.$toast.success(this.$t('partner_updated') || 'Partenaire mis à jour avec succès');
            this.$router.push('/partners');
          }
        } else {
          response = await this.$server.create('partners', this.formData);
          if (response && response.content) {
            this.createdCredentials = response.content.credentials;
            this.showSuccessModal = true;
          }
        }
      } catch (error) {
        console.error('Error saving partner:', error);
        this.$toast.error(error.message || this.$t('error_saving_partner') || 'Erreur lors de l\'enregistrement');
      } finally {
        this.saving = false;
      }
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.$router.push('/partners');
    }
  }
};
</script>
